<template>
  <el-dialog v-model="visible" :title="`履约能力与排班 - ${provider?.realName || ''}`" width="980px">
    <el-tabs v-model="activeTab">
      <el-tab-pane label="服务能力" name="capability">
        <el-form label-width="110px">
          <el-form-item label="可服务品类" required>
            <el-tree-select
              v-model="categoryIds"
              :data="categoryOptions"
              multiple
              show-checkbox
              check-strictly
              node-key="value"
              :props="{ label: 'label', children: 'children' }"
              class="!w-1/1"
            />
          </el-form-item>
          <el-form-item label="服务白名单">
            <el-select
              v-model="serviceIds"
              multiple
              filterable
              class="!w-1/1"
              placeholder="仅服务配置为白名单时需要选择"
            >
              <el-option
                v-for="service in serviceOptions"
                :key="service.id"
                :label="service.name"
                :value="service.id"
              />
            </el-select>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="师傅排班" name="schedule">
        <div class="mb-12px flex flex-wrap items-center gap-12px">
          <el-date-picker
            v-model="weekStart"
            type="date"
            value-format="YYYY-MM-DD"
            @change="loadSchedule"
          />
          <el-button @click="applyWorkWeek">工作日模板</el-button>
          <el-button @click="copyNextWeek">复制到下一周</el-button>
          <el-button type="warning" @click="setSelectedDayOff">所选日休息</el-button>
          <el-button type="primary" plain @click="openAddSlot">添加时段</el-button>
        </div>

        <div class="mb-12px flex flex-wrap items-center gap-8px">
          <span class="text-13px text-gray-500">当前时段方案：</span>
          <el-tag
            v-for="slot in slotTemplates"
            :key="slot"
            class="!mr-0"
            closable
            :disable-transitions="true"
            @close="removeSlotTemplate(slot)"
          >
            {{ slot }}
          </el-tag>
          <span v-if="!slotTemplates.length" class="text-13px text-gray-400">暂无时段，请先添加</span>
        </div>
        <el-alert
          class="mb-12px"
          :closable="false"
          show-icon
          type="info"
          title="可自定义任意 HH:mm-HH:mm 时段。用户端只会看到已「开放」且有容量的时段。"
        />

        <el-table :data="scheduleRows" border max-height="440px">
          <el-table-column label="日期" width="130">
            <template #default="{ row }">
              <el-radio v-model="selectedDate" :value="row.workDate">{{ row.workDate }}</el-radio>
            </template>
          </el-table-column>
          <el-table-column label="时段" width="160" prop="timeSlot" />
          <el-table-column label="容量" width="150">
            <template #default="{ row }">
              <el-input-number v-model="row.capacity" :min="row.booked" :max="10" />
            </template>
          </el-table-column>
          <el-table-column label="已约" width="90" prop="booked" />
          <el-table-column label="开放">
            <template #default="{ row }">
              <el-switch v-model="row.open" />
            </template>
          </el-table-column>
        </el-table>
        <el-empty
          v-if="!scheduleRows.some((row) => row.open)"
          description="本周暂无开放排班，可添加时段后打开「开放」开关"
        />
      </el-tab-pane>
    </el-tabs>
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </template>
  </el-dialog>

  <el-dialog v-model="addSlotVisible" title="添加自定义时段" width="420px" append-to-body>
    <el-form label-width="88px">
      <el-form-item label="开始时间" required>
        <el-time-select
          v-model="addSlotForm.start"
          class="!w-1/1"
          start="00:00"
          step="00:30"
          end="23:30"
          placeholder="开始"
        />
      </el-form-item>
      <el-form-item label="结束时间" required>
        <el-time-select
          v-model="addSlotForm.end"
          class="!w-1/1"
          start="00:30"
          step="00:30"
          end="23:30"
          placeholder="结束"
        />
      </el-form-item>
      <el-form-item label="应用到">
        <el-radio-group v-model="addSlotForm.scope">
          <el-radio value="week">本周每天</el-radio>
          <el-radio value="day">仅所选日</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="默认开放">
        <el-switch v-model="addSlotForm.open" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="addSlotVisible = false">取消</el-button>
      <el-button type="primary" @click="confirmAddSlot">确定</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import request, { merchantApi } from '@/config/axios'
import {
  DEFAULT_PROVIDER_SLOTS,
  formatSlot,
  slotKey,
  sortSlots,
  validateSlotRange
} from '../utils/scheduleSlots'

const props = defineProps<{ merchant?: boolean }>()
const message = useMessage()

const visible = ref(false)
const saving = ref(false)
const activeTab = ref('capability')
const provider = ref<any>()
const categoryIds = ref<number[]>([])
const serviceIds = ref<number[]>([])
const categoryOptions = ref<any[]>([])
const serviceOptions = ref<any[]>([])
const selectedDate = ref('')
const weekStart = ref(dayjs().startOf('week').add(1, 'day').format('YYYY-MM-DD'))
const scheduleRows = ref<any[]>([])
const slotTemplates = ref<string[]>([...DEFAULT_PROVIDER_SLOTS])
const serverSlotKeys = ref<string[]>([])

const addSlotVisible = ref(false)
const addSlotForm = reactive({
  start: '09:00',
  end: '11:00',
  scope: 'week' as 'week' | 'day',
  open: true
})

function getApi<T = any>(url: string, params?: any): Promise<T> {
  return props.merchant
    ? merchantApi.get<T>({ url: `/merchant-api${url}`, params })
    : request.get({ url, params })
}

function putApi(url: string, data: any) {
  return props.merchant
    ? merchantApi.put({ url: `/merchant-api${url}`, data })
    : request.put({ url, data })
}

function mapCategoryTree(nodes: any[]): any[] {
  return (nodes || []).map((node) => ({
    value: node.id,
    label: node.name,
    disabled: node.leaf === false,
    children: mapCategoryTree(node.children)
  }))
}

function weekDates(): string[] {
  return Array.from({ length: 7 }).map((_, day) =>
    dayjs(weekStart.value).add(day, 'day').format('YYYY-MM-DD')
  )
}

function rebuildRows(preserve?: Map<string, any>) {
  const map = preserve || new Map(scheduleRows.value.map((r) => [slotKey(r.workDate, r.timeSlot), r]))
  scheduleRows.value = weekDates().flatMap((workDate) =>
    slotTemplates.value.map((timeSlot) => {
      const hit = map.get(slotKey(workDate, timeSlot))
      return hit
        ? { ...hit, workDate, timeSlot }
        : { workDate, timeSlot, capacity: 1, booked: 0, open: false }
    })
  )
}

async function loadOptions() {
  const categories: any = await getApi(
    props.merchant ? '/o2o/institution/apply/category-tree' : '/o2o/catalog/category/tree'
  )
  categoryOptions.value = mapCategoryTree(categories?.tree || categories || [])
  try {
    const services: any = await getApi(
      props.merchant ? '/o2o/pricing/service-options' : '/o2o/catalog/service/page',
      { pageNo: 1, pageSize: 200 }
    )
    serviceOptions.value = services?.list || services || []
  } catch {
    serviceOptions.value = []
  }
}

async function loadSchedule() {
  if (!provider.value?.id) {
    slotTemplates.value = [...DEFAULT_PROVIDER_SLOTS]
    rebuildRows(new Map())
    return
  }
  const list: any[] = await getApi('/o2o/schedule/provider/list', {
    providerId: provider.value.id,
    workDateFrom: weekStart.value,
    workDateTo: dayjs(weekStart.value).add(6, 'day').format('YYYY-MM-DD')
  })
  serverSlotKeys.value = (list || []).map((s) => slotKey(s.workDate, s.timeSlot))
  const fromDb = [...new Set((list || []).map((s) => s.timeSlot).filter(Boolean))]
  slotTemplates.value = sortSlots(
    fromDb.length ? fromDb : [...DEFAULT_PROVIDER_SLOTS]
  )
  const preserve = new Map<string, any>()
  ;(list || []).forEach((slot) => {
    preserve.set(slotKey(slot.workDate, slot.timeSlot), {
      ...slot,
      open: slot.status !== 'CLOSED'
    })
  })
  rebuildRows(preserve)
  if (!selectedDate.value) {
    selectedDate.value = weekStart.value
  }
}

async function open(row: any) {
  provider.value = row
  visible.value = true
  activeTab.value = 'capability'
  await loadOptions()
  const [categories, services]: any = await Promise.all([
    getApi('/o2o/provider/categories', { id: row.id }),
    getApi('/o2o/provider/services', { id: row.id })
  ])
  categoryIds.value = categories || []
  serviceIds.value = services || []
  await loadSchedule()
}

function openAddSlot() {
  addSlotForm.start = '09:00'
  addSlotForm.end = '11:00'
  addSlotForm.scope = selectedDate.value ? 'day' : 'week'
  addSlotForm.open = true
  addSlotVisible.value = true
}

function confirmAddSlot() {
  const err = validateSlotRange(addSlotForm.start, addSlotForm.end)
  if (err) {
    message.warning(err)
    return
  }
  const timeSlot = formatSlot(addSlotForm.start, addSlotForm.end)
  if (slotTemplates.value.includes(timeSlot)) {
    message.warning('该时段已存在')
    return
  }
  slotTemplates.value = sortSlots([...slotTemplates.value, timeSlot])
  const dates =
    addSlotForm.scope === 'day' && selectedDate.value
      ? [selectedDate.value]
      : weekDates()
  const map = new Map(scheduleRows.value.map((r) => [slotKey(r.workDate, r.timeSlot), r]))
  dates.forEach((workDate) => {
    map.set(slotKey(workDate, timeSlot), {
      workDate,
      timeSlot,
      capacity: 1,
      booked: 0,
      open: addSlotForm.open
    })
  })
  rebuildRows(map)
  addSlotVisible.value = false
  message.success(`已添加时段 ${timeSlot}`)
}

async function removeSlotTemplate(timeSlot: string) {
  const booked = scheduleRows.value
    .filter((r) => r.timeSlot === timeSlot)
    .reduce((sum, r) => sum + (Number(r.booked) || 0), 0)
  if (booked > 0) {
    message.warning(`时段 ${timeSlot} 已有预约，不能删除；可改为关闭开放`)
    return
  }
  try {
    await message.confirm(`确认删除时段「${timeSlot}」？本周该时段将关闭并不再展示。`)
  } catch {
    return
  }
  slotTemplates.value = slotTemplates.value.filter((s) => s !== timeSlot)
  rebuildRows()
}

function applyWorkWeek() {
  if (!slotTemplates.value.length) {
    message.warning('请先添加时段')
    return
  }
  scheduleRows.value.forEach((row) => {
    const weekday = dayjs(row.workDate).day()
    row.open = weekday !== 0 && weekday !== 6
    row.capacity = Math.max(row.booked, 1)
  })
}

function setSelectedDayOff() {
  if (!selectedDate.value) {
    message.warning('请先点选左侧某一天')
    return
  }
  scheduleRows.value
    .filter((row) => row.workDate === selectedDate.value)
    .forEach((row) => {
      row.open = false
    })
}

function copyNextWeek() {
  scheduleRows.value.push(
    ...scheduleRows.value.map((row) => ({
      ...row,
      workDate: dayjs(row.workDate).add(7, 'day').format('YYYY-MM-DD'),
      booked: 0
    }))
  )
}

async function save() {
  if (!categoryIds.value.length) {
    message.warning('请至少选择一个可服务品类')
    activeTab.value = 'capability'
    return
  }
  saving.value = true
  try {
    await putApi('/o2o/provider/categories', {
      providerId: provider.value.id,
      ids: categoryIds.value
    })
    await putApi('/o2o/provider/services', {
      providerId: provider.value.id,
      ids: serviceIds.value
    })
    const currentKeys = new Set(
      scheduleRows.value.map((row) => slotKey(row.workDate, row.timeSlot))
    )
    const slots = scheduleRows.value.map((row) => ({
      workDate: row.workDate,
      timeSlot: row.timeSlot,
      capacity: row.capacity,
      status: row.open ? 'AVAILABLE' : 'CLOSED'
    }))
    // 已从界面移除的旧时段 → 关闭，避免 C 端仍可约
    serverSlotKeys.value.forEach((key) => {
      if (currentKeys.has(key)) return
      const [workDate, timeSlot] = key.split('|')
      slots.push({
        workDate,
        timeSlot,
        capacity: 1,
        status: 'CLOSED'
      })
    })
    await putApi('/o2o/schedule/provider/batch', {
      providerId: provider.value.id,
      storeId: provider.value.storeId,
      slots
    })
    message.success('能力与排班已保存')
    visible.value = false
  } finally {
    saving.value = false
  }
}

defineExpose({ open })
</script>
